import { useState, useRef, useCallback } from "react";
import { Send } from "lucide-react";
import clsx from "clsx";

function truncateAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

interface CommentInputProps {
  onSubmit: (body: string) => void;
  loading: boolean;
  canComment: boolean;
  isConnected: boolean;
  replyingToAddress?: string | null;
  onCancelReply?: () => void;
  commenterAddresses: string[];
}

export function CommentInput({
  onSubmit,
  loading,
  canComment,
  isConnected,
  replyingToAddress,
  onCancelReply,
  commenterAddresses,
}: CommentInputProps) {
  const [body, setBody] = useState("");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const filteredAddresses = mentionQuery !== null
    ? commenterAddresses.filter((a) =>
        a.toLowerCase().startsWith(mentionQuery.toLowerCase()),
      )
    : [];

  const showDropdown = mentionQuery !== null && filteredAddresses.length > 0;

  const detectMention = useCallback((text: string, cursorPos: number) => {
    const before = text.slice(0, cursorPos);
    let wordStart = cursorPos;
    while (wordStart > 0 && !/\s/.test(before[wordStart - 1])) {
      wordStart--;
    }
    const currentWord = before.slice(wordStart);
    if (currentWord.startsWith("0x") && currentWord.length >= 2) {
      setMentionQuery(currentWord);
      setMentionStart(wordStart);
      setSelectedIndex(0);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const insertMention = useCallback(
    (address: string) => {
      const before = body.slice(0, mentionStart);
      const afterCursor = textareaRef.current
        ? body.slice(textareaRef.current.selectionStart)
        : body.slice(mentionStart + (mentionQuery?.length || 0));
      const truncated = truncateAddress(address);
      const newBody = `${before}${truncated} ${afterCursor}`;
      setBody(newBody);
      setMentionQuery(null);
      setTimeout(() => {
        if (textareaRef.current) {
          const pos = before.length + truncated.length + 1;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(pos, pos);
        }
      }, 0);
    },
    [body, mentionStart, mentionQuery],
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBody(e.target.value);
    detectMention(e.target.value, e.target.selectionStart);
  };

  const handleSelect = () => {
    if (textareaRef.current) {
      detectMention(body, textareaRef.current.selectionStart);
    }
  };

  const handleSubmit = () => {
    const trimmed = body.trim();
    if (!trimmed || loading) return;
    onSubmit(trimmed);
    setBody("");
    setMentionQuery(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredAddresses.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredAddresses[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // If canComment is true (e.g. writer on own article), show the input regardless of wallet.
  // Otherwise, check wallet connection first, then payment status.
  if (!canComment && !isConnected) {
    return (
      <div className="py-3 text-center">
        <p className="text-sm text-grey font-body">Connect your wallet to comment</p>
      </div>
    );
  }

  if (!canComment) {
    return (
      <div className="py-3 text-center">
        <p className="text-sm text-grey font-body">Pay for this article to join the discussion</p>
      </div>
    );
  }

  return (
    <div className="py-3">
      {replyingToAddress && (
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-xs text-grey font-body">
            Replying to <span className="text-orange font-mono">{truncateAddress(replyingToAddress)}</span>
          </span>
          <button
            onClick={onCancelReply}
            className="text-xs text-orange hover:text-orange/80 font-body"
          >
            Cancel
          </button>
        </div>
      )}
      <div className="relative">
        {showDropdown && (
          <div className="absolute bottom-full left-0 mb-1 w-full max-h-48 overflow-y-auto bg-white border border-beige/30 rounded-lg shadow-lg z-20">
            {filteredAddresses.map((address, i) => (
              <button
                key={address}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(address);
                }}
                className={clsx(
                  "w-full text-left px-3 py-2 transition-colors",
                  i === selectedIndex
                    ? "bg-orange/10"
                    : "hover:bg-black/5",
                )}
              >
                <span className="text-orange text-sm font-mono">{truncateAddress(address)}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onSelect={handleSelect}
            placeholder="Write a comment..."
            maxLength={2000}
            rows={1}
            className={clsx(
              "flex-1 resize-none px-3 py-2 rounded-lg border border-beige/40 bg-white",
              "font-body text-sm placeholder:text-beige",
              "focus:outline-none focus:ring-2 focus:ring-orange/50 focus:border-orange",
            )}
          />
          <button
            onClick={handleSubmit}
            disabled={!body.trim() || loading}
            className={clsx(
              "p-2 rounded-lg transition-colors",
              body.trim() && !loading
                ? "bg-black text-cream hover:bg-brown"
                : "bg-beige/20 text-beige cursor-not-allowed",
            )}
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
